import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import { useIdleTimeoutContext } from './IdleTimeoutContext';

const SessionTimeoutModal: React.FC = () => {
  const { isPrompted, getRemainingTime, timeout, extendTime, onExtend } =
    useIdleTimeoutContext();
  const [remaining, setRemaining] = useState<number>(0);
  const { t } = useTranslation();

  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | undefined;

    if (isPrompted) {
      let time = Math.ceil(getRemainingTime() / 1000);
      // Set initial remaining time
      setRemaining(time);

      // Update countdown every second
      interval = setInterval(() => {
        time--;
        setRemaining(time > 0 ? time : 0);
      }, 1000);
    }

    // Cleanup function to clear the interval when the component unmounts or isPrompted changes
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isPrompted, getRemainingTime]);

  // Helper function to format the time for display
  const formatTime = (timeInSeconds: number): string => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = timeInSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds
      .toString()
      .padStart(2, '0')}`;
  };

  return (
    <Dialog
      open={isPrompted}
      onClose={() => null}
      aria-labelledby="idle-dialog-title"
      aria-describedby="idle-dialog-description"
    >
      <DialogTitle id="idle-dialog-title">
        {t('pages.idlePage.title')}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {t('pages.idlePage.description', {
            time: Math.ceil(timeout / (1000 * 60 * 60)),
          })}
        </DialogContentText>
        <DialogContentText id="idle-dialog-description" component="div">
          {t('pages.idlePage.reloadMessage', {
            time: formatTime(remaining),
          })}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => window.location.reload()} color="secondary">
          {t('pages.idlePage.reload', 'Reload')}
        </Button>
        <Button
          onClick={onExtend}
          color="primary"
          variant="contained"
          autoFocus
        >
          {t('pages.idlePage.wait', {
            time: Math.ceil(extendTime / (1000 * 60)),
          })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionTimeoutModal;
